import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Checkbox } from "primereact/checkbox";
import { Tooltip } from "primereact/tooltip";
import { Toast } from "primereact/toast";
import useAuth from "../../hooks/useAuth";
import { EmailComposeDialog } from "./EmailComposeDialog";

interface LeadSummaryDetailDialogProps {
  visible: boolean;
  onHide: () => void;
  selectedRowData: any;
  loadingSummaryDetails: boolean;
  summaryDetails: any[];
  onToggleActivity: (
    leadId: number,
    emailSent?: boolean,
    callMade?: boolean,
    emailDescription?: string,
    callDescription?: string
  ) => void;
  onViewLeadDetails: (leadId: number) => void;
}

export const LeadSummaryDetailDialog: React.FC<LeadSummaryDetailDialogProps> = ({
  visible,
  onHide,
  selectedRowData,
  loadingSummaryDetails,
  summaryDetails,
  onToggleActivity,
  onViewLeadDetails,
}) => {
  const { loginObj } = useAuth();

  const teamMemberId = selectedRowData?.teamMemberId ?? summaryDetails[0]?.teamMemberId;
  const isAssignedToCurrentUser = !!(
    teamMemberId &&
    loginObj &&
    Number(teamMemberId) === Number(loginObj.id)
  );

  const [descVisible, setDescVisible] = useState(false);
  const [descText, setDescText] = useState("");
  const [pendingLead, setPendingLead] = useState<any>(null);
  const [pendingType, setPendingType] = useState<"email" | "call" | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const [composeVisible, setComposeVisible] = useState(false);
  const [composeLead, setComposeLead] = useState<any>(null);
  const toastRef = useRef<any>(null);

  const [filterEmail, setFilterEmail] = useState(false);
  const [filterCall, setFilterCall] = useState(false);

  useEffect(() => {
    if (!visible) {
      setFilterEmail(false);
      setFilterCall(false);
    }
  }, [visible]);

  const filteredSummaryDetails = summaryDetails.filter((item) => {
    if (filterEmail && item.emailSent !== "Yes") return false;
    if (filterCall && item.callMade !== "Yes") return false;
    return true;
  });

  const openDescModal = (lead: any, type: "email" | "call") => {
    setPendingLead(lead);
    setPendingType(type);
    setDescText(
      type === "email"
        ? lead.emailDescription || ""
        : lead.callDescription || ""
    );
    setSubmitted(false);
    setDescVisible(true);
  };

  const handleDescSave = () => {
    setSubmitted(true);
    if (!descText || !descText.trim()) {
      toastRef.current?.show({
        severity: "error",
        summary: "Required Field",
        detail: "Activity Notes / Description is required.",
        life: 3000,
      });
      return;
    }
    if (pendingLead && pendingType) {
      if (pendingType === "email") {
        onToggleActivity(pendingLead.id, true, undefined, descText, undefined);
      } else {
        onToggleActivity(pendingLead.id, undefined, true, undefined, descText);
      }
    }
    setDescVisible(false);
    setPendingLead(null);
    setPendingType(null);
    setDescText("");
    setSubmitted(false);
  };

  const handleDescCancel = () => {
    setDescVisible(false);
    setPendingLead(null);
    setPendingType(null);
    setDescText("");
    setSubmitted(false);
  };

  const handleEmailChange = (rowData: any, value: string) => {
    if (value === "Yes") {
      setComposeLead(rowData);
      setComposeVisible(true);
    } else {
      onToggleActivity(rowData.id, false, undefined, "", undefined);
    }
  };

  const handleCallChange = (rowData: any, value: string) => {
    if (value === "Yes") {
      openDescModal(rowData, "call");
    } else {
      onToggleActivity(rowData.id, undefined, false, undefined, "");
    }
  };

  const yesNoOptions = [
    { label: "Yes", value: "Yes" },
    { label: "No", value: "No" },
  ];

  const headerText = `Allocated Contacts Detail - ${selectedRowData?.teamMemberName || ""} (${selectedRowData?.allocationDateTime ? new Date(selectedRowData.allocationDateTime).toLocaleDateString() : ""
    })`;

  const descDialogFooter = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancel"
        // icon="pi pi-times"
        className="p-button-text p-button-secondary"
        onClick={handleDescCancel}
      />
      <Button
        label="Save"
        // icon="pi pi-check"
        className="p-button-success"
        onClick={handleDescSave}
      />
    </div>
  );

  const stripHtml = (html?: string) => {
    if (!html) return "";

    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };
  return (
    <Dialog
      header={headerText}
      visible={visible}
      style={{ width: "1000px" }}
      onHide={onHide}
      modal
      className="p-fluid"
    >
      {loadingSummaryDetails ? (
        <div className="flex justify-content-center align-items-center py-5">
          <i className="pi pi-spin pi-spinner text-primary text-3xl"></i>
        </div>
      ) : (
        <div className="flex flex-column gap-3">
          <div className="flex align-items-center gap-4 p-2 bg-bluegray-50 border-round border-1 border-gray-200">
            <span className="font-semibold text-sm text-color-secondary">Filter Activities:</span>
            <div className="flex align-items-center gap-2">
              <Checkbox
                inputId="emailSentFilter"
                checked={filterEmail}
                onChange={(e) => setFilterEmail(e.checked ?? false)}
              />
              <label htmlFor="emailSentFilter" className="text-sm font-medium cursor-pointer">
                Email Sent
              </label>
            </div>
            <div className="flex align-items-center gap-2">
              <Checkbox
                inputId="callMadeFilter"
                checked={filterCall}
                onChange={(e) => setFilterCall(e.checked ?? false)}
              />
              <label htmlFor="callMadeFilter" className="text-sm font-medium cursor-pointer">
                Call Completed
              </label>
            </div>
            {(filterEmail || filterCall) && (
              <Button
                label="Clear"
              //  icon="pi pi-filter-slash"
                className="p-button-outlined p-button-sm p-button-secondary ml-auto"
                style={{ width: "auto" }}
                onClick={() => {
                  setFilterEmail(false);
                  setFilterCall(false);
                }}
              />
            )}
          </div>
          <DataTable
            value={filteredSummaryDetails}
            stripedRows
            showGridlines
            responsiveLayout="scroll"
            emptyMessage="No contacts found for this allocation date."
            size="small"
          >
            <Column field="contactName" header="Contact Name" className="font-semibold" />
            <Column field="email" header="Email" />
            <Column field="phone" header="Phone Number" />
            <Column
              header="Email Sent (Yes/No)"
              className="text-center"
              style={{ width: "15%" }}
              body={(rowData: any) => {
                const hasEmailDesc = !!rowData.emailDescription;
                return isAssignedToCurrentUser ? (
                  <div
                    className={hasEmailDesc ? "email-desc-tooltip" : ""}
                    data-pr-tooltip={stripHtml(rowData.emailDescription || "")}
                  >
                    <Dropdown
                      value={rowData.emailSent === "Yes" ? "Yes" : "No"}
                      options={yesNoOptions}
                      onChange={(e) => handleEmailChange(rowData, e.value)}
                      style={{ width: "100%" }}
                    />
                  </div>
                ) : (
                  <span
                    className={`font-semibold px-3 py-1 border-round text-sm ${rowData.emailSent === "Yes" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                      } ${hasEmailDesc ? "email-desc-tooltip cursor-pointer" : ""}`}
                    data-pr-tooltip={stripHtml(rowData.emailDescription || "")}
                  >
                    {rowData.emailSent === "Yes" ? "Yes" : "No"}
                  </span>
                );
              }}
            />
            <Column
              header="Call Completed (Yes/No)"
              className="text-center"
              style={{ width: "18%" }}
              body={(rowData: any) => {
                const hasCallDesc = !!rowData.callDescription;
                return isAssignedToCurrentUser ? (
                  <div
                    className={hasCallDesc ? "call-desc-tooltip" : ""}
                    data-pr-tooltip={rowData.callDescription || ""}
                  >
                    <Dropdown
                      value={rowData.callMade === "Yes" ? "Yes" : "No"}
                      options={yesNoOptions}
                      onChange={(e) => handleCallChange(rowData, e.value)}
                      style={{ width: "100%" }}
                    />
                  </div>
                ) : (
                  <span
                    className={`font-semibold px-3 py-1 border-round text-sm ${rowData.callMade === "Yes" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                      } ${hasCallDesc ? "call-desc-tooltip cursor-pointer" : ""}`}
                    data-pr-tooltip={rowData.callDescription || ""}
                  >
                    {rowData.callMade === "Yes" ? "Yes" : "No"}
                  </span>
                );
              }}
            />
            <Column
              header="Date Allocated"
              body={(rowData: any) =>
                rowData.allocationDateTime ? new Date(rowData.allocationDateTime).toLocaleDateString() : "-"
              }
              style={{ width: "15%" }}
            />
            <Column
              header="Action"
              className="text-center"
              style={{ width: "15%" }}
              body={(rowData: any) => (
                <Button
                  label="View"
                  //icon="pi pi-eye"
                  className="p-button-sm p-button-rounded p-button-text p-button-info"
                  onClick={() => onViewLeadDetails(rowData.id)}
                />
              )}
            />
          </DataTable>
        </div>
      )}

      <Dialog
        header={`Add Description for ${pendingType === "email" ? "Email Sent" : "Call Completed"}`}
        visible={descVisible}
        style={{ width: "450px" }}
        footer={descDialogFooter}
        onHide={handleDescCancel}
        modal
      >
        <div className="flex flex-column gap-2 mt-2">
          <label htmlFor="activity-description" className="font-semibold text-800 text-sm">
            Activity Notes / Description <span style={{ color: 'red' }}>*</span>
          </label>
          <InputTextarea
            id="activity-description"
            value={descText}
            onChange={(e) => setDescText(e.target.value)}
            rows={4}
            autoResize
            placeholder="Type activity details here..."
            className={`w-full ${submitted && !descText.trim() ? "p-invalid" : ""}`}
            autoFocus
          />
          {submitted && !descText.trim() && (
            <small className="p-error">Description is required.</small>
          )}
        </div>
      </Dialog>
      <Toast ref={toastRef} />
      <EmailComposeDialog
        visible={composeVisible}
        onHide={() => {
          setComposeVisible(false);
          setComposeLead(null);
        }}
        lead={composeLead}
        toast={toastRef}
        onSuccess={(emailSent, description) => {
          onToggleActivity(composeLead.id, emailSent, undefined, description, undefined);
        }}
      />
      <Tooltip target=".email-desc-tooltip" position="top" />
      <Tooltip target=".call-desc-tooltip" position="top" />
    </Dialog>
  );
};
